/**
 * Typed client for the DBIndex control-plane API (docs/architecture/INTERFACES.md §7)
 * and the data-plane query API (§6).
 *
 * - Session is an HttpOnly cookie; this module never sees credentials after submit.
 * - All mutating control-plane requests carry `X-DBIndex-CSRF: 1`.
 * - Data-plane calls use a consumer API key passed per call and held only in memory
 *   by the caller (never persisted, never logged).
 * - Money crosses the wire as decimal strings ("0.001200").
 */
import type { ColumnClassification, DatasetManifest, ExposureContract, Freshness, QueryPlan, SchemaSnapshot, SignedEnvelope } from "@dbindex/contracts";
import { formatMicros } from "@dbindex/contracts";
import type { MappingDraft, MappingSuggestion } from "@dbindex/architect";

export type Role = "owner" | "admin" | "data_engineer" | "security_reviewer" | "billing_admin" | "developer" | "viewer";
export const ROLES: Role[] = ["owner", "admin", "data_engineer", "security_reviewer", "billing_admin", "developer", "viewer"];
export type OrgKind = "provider" | "consumer";
export type DecimalString = string;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string | null,
    public readonly retryable = false,
    /** Structured error details from the server, e.g. `demand` (waitlist hint) on no_route. */
    public readonly details: Record<string, unknown> | null = null,
  ) {
    super(message);
  }
}

// ---------- shared shapes ----------
export interface User { id: string; email: string; name: string; createdAt?: string }
export interface Membership { orgId: string; orgName: string; role: Role; kinds: OrgKind[] }
export interface Me { user: User; memberships: Membership[]; platformAdmin?: boolean }
export interface Org { id: string; name: string; kinds: OrgKind[]; createdAt?: string }

export type SourceEngine = "postgres" | "mysql" | "mariadb" | "sqlserver" | "oracle" | "sqlite" | "ingres" | "other";
export type Connectivity = "connector" | "tunnel" | "direct";

export interface Source {
  id: string;
  name: string;
  engine: SourceEngine | string;
  connectivity: Connectivity;
  status: string;
  createdAt: string;
  connectorId?: string | null;
}
export interface Connector {
  id: string;
  status: "pending" | "online" | "offline" | "revoked" | string;
  version?: string | null;
  hostname?: string | null;
  adapters?: string[] | string | null;
  enrolledAt?: string | null;
  lastHeartbeatAt?: string | null;
  heartbeat?: {
    uptimeSeconds?: number;
    datasets?: { datasetId: string; state: string; lagSeconds: number | null; recordsPerSec: number; lastError?: string | null }[];
  } | null;
}
export interface Job {
  id: string;
  type: string;
  status: "queued" | "leased" | "done" | "failed" | string;
  result?: unknown;
  error?: string | null;
  createdAt: string;
  completedAt?: string | null;
}
export interface SetupInstructions { dockerRun?: string; compose?: string; kubernetes?: string; binary?: string }
/** Built into the one-step connect command in the browser; the customer's DSN never leaves it. */
export interface ConnectInfo { apiBase: string; image: string; insecureDev: boolean }
/** Outbound connector: enrollment + setup. Hosted ("DBIndex connects for you"): `hosted: true`, nothing to run. */
export interface CreateSourceResponse { source: Source; enrollment?: { token: string; expiresAt: string }; setup?: SetupInstructions; connect?: ConnectInfo; hosted?: boolean }
export interface SourceDetail {
  source: Source;
  connector: Connector | null;
  jobs: Job[];
  datasets: DatasetSummary[];
  topology?: Record<string, { status: string; detail?: string }>;
}

export interface ArchitectTable {
  schema: string;
  table: string;
  rowEstimate: number;
  businessEntity: string;
  interpretation: string;
  commercialUsefulness: number;
  recommendedServingEntity: string | null;
  columns: ColumnClassification[];
}
export interface ArchitectProposal {
  generatedAt: string;
  model: string | null;
  engine: string;
  tables: ArchitectTable[];
  quasiIdentifierWarnings: { table: string; columns: string[]; reason: string }[];
  proposedContract: Pick<ExposureContract, "entities" | "capabilities" | "freshness" | "cache" | "rights" | "mode">;
  recommendations: {
    freshness: Freshness;
    cacheTtlSeconds: number;
    servingStrategy: string;
    priceCategory: "commodity" | "standard" | "premium";
    indexes: { entity: string; fields: string[] }[];
  };
  notes: string[];
}

export type DatasetLifecycle = "draft" | "pending_approval" | "approved" | "syncing" | "active" | "paused" | "retired" | string;
export interface DatasetSummary {
  id: string;
  name: string;
  description?: string;
  status: DatasetLifecycle;
  sourceId?: string;
  categories?: string[];
  keywords?: string[];
  geographic?: string[];
  freshness?: Freshness;
  pricePerQuery?: DecimalString;
  freshnessStatus?: DatasetStatus | null;
  activeContractVersion?: number | null;
  createdAt?: string;
  queries30d?: number;
  revenue30d?: DecimalString;
  /** "private": only your own organisation can query it (included in your plan). "network": listed for AI agents. */
  visibility?: "private" | "network";
}
export interface DatasetStatus {
  datasetId: string;
  state: "syncing" | "active" | "stale" | "error";
  rowCount: number;
  lastAppliedAt: string | null;
  lastSourceTs: string | null;
  lagSeconds: number | null;
  lagBreakdownMs?: { sourceToConnector: number; connectorToIngest: number; ingestToIndexed: number };
  updatedAt: string;
}
export interface ContractVersionInfo {
  version: number;
  status: "draft" | "approved" | "active" | "superseded" | string;
  createdAt: string;
  createdBy?: string;
  approvedBy?: { userId: string; role: string; at: string }[];
}
export interface DatasetDetail {
  /** Initial copy progress: complete once every approved entity finished its snapshot. */
  snapshot?: { entities: string[]; done: string[]; complete: boolean | null; rows?: Record<string, number> };
  dataset: DatasetSummary;
  contract: ExposureContract | null;
  versions: ContractVersionInfo[];
  manifest: DatasetManifest | SignedEnvelope<DatasetManifest> | null;
  status: DatasetStatus | null;
  approvals?: { version: number; userId: string; role: string; at: string }[];
  contractMeta?: { version: number; status: string; hash: string; violations: string[] } | null;
}
export interface ContractChange { path: string; kind: "added" | "removed" | "changed"; before?: unknown; after?: unknown; exposureImpact: "increases" | "decreases" | "neutral" }
export interface EarningsWindow { queries: number; paidQueries: number; gross: DecimalString; fee: DecimalString; revenue: DecimalString; servingCost: DecimalString; net: DecimalString; payingConsumers: number }
export interface DatasetEarnings { datasetId: string; since: string; allTime: EarningsWindow; last30d: EarningsWindow; today: EarningsWindow; firstEarningAt: string | null; lastEarningAt: string | null }
export interface PutContractResponse {
  version: number;
  diff: { changes: ContractChange[]; requiresResync: boolean; requiresReindex: boolean; breaking: boolean; migrationPlan: string[] } | null;
  migrationPlan?: string[];
  violations: string[];
}
export interface ExposureReportEntity {
  entity: string;
  source: string;
  rowFilters: unknown[];
  shared: { source: string; serving: string | null; sensitivity: string }[];
  transformed: { source: string; serving: string | null; transform: { kind: string }; sensitivity: string }[];
  blocked: { source: string; sensitivity: string }[];
}
export interface SimulateResponse { answerable: boolean; explanation: string[]; blockedFieldsReferenced?: string[]; plan?: QueryPlan }

export interface PriceRecommendation {
  exclusivity: { score: number; tier: "commodity" | "common" | "differentiated" | "rare" | "exclusive"; similarCount: number; message?: string };
  recommended: [DecimalString, DecimalString];
  competitive: [DecimalString, DecimalString];
  premium: [DecimalString, DecimalString];
  winRate: { price: DecimalString; winRate: number }[];
  forecast?: { low: number; base: number; high: number; modelled: true; drivers: string[] };
  currentPrice?: DecimalString;
  disclaimer: string;
}
export interface CostBreakdownRow { product: string; quantity: number; unit: string; cost: DecimalString; note: string }
export interface CostEstimateResponse {
  low: DecimalString;
  high: DecimalString;
  expected: DecimalString;
  breakdown: CostBreakdownRow[];
  assumptions: string[];
  tableVersion: string;
  scenarios?: { qpm: number; queriesPerMonth: number; gross: DecimalString; fee: DecimalString; providerGross: DecimalString; hosting: DecimalString; net: DecimalString }[];
}

export interface UsageEventRow {
  queryId: string;
  ts: string;
  datasetId: string;
  capability: string;
  consumerOrgId?: string;
  price: DecimalString;
  providerShare?: DecimalString;
  cacheStatus: string;
  latencyMs: number;
  rows: number;
  status: string;
  routingReason?: string | null;
}

export interface MetricOverview {
  provider?: {
    dataProducts: number;
    qpm: number;
    queries24h: number;
    revenue30d: DecimalString;
    hostingCost30d: DecimalString;
    netEarnings30d: DecimalString;
    avgFreshnessSeconds: number | null;
    cacheHitRate: number | null;
  };
  consumer?: {
    queries30d: number;
    spend30d: DecimalString;
    avgPrice: DecimalString;
    p95LatencyMs: number | null;
    cacheHitRate: number | null;
    datasetsUsed: number;
    providerCount: number;
    balance?: DecimalString;
  };
}

export interface DailyPoint {
  date: string;
  queries: number;
  cacheHits?: number;
  errors?: number;
  gross?: DecimalString;
  fee?: DecimalString;
  net?: DecimalString;
  hostingCost?: DecimalString;
  spend?: DecimalString;
  latencyMsAvg?: number | null;
}
export interface RevenueResponse {
  days: number;
  series: DailyPoint[];
  byDataset: { datasetId: string; name?: string; queries: number; gross: DecimalString; net?: DecimalString }[];
  /** Pseudonymised per provider (consumer_<hash>); providers never see consumer identities. */
  byConsumer?: { consumerOrgId: string; name?: string; queries: number; gross: DecimalString }[];
  totals?: { queries: number; gross: DecimalString; fee: DecimalString; net: DecimalString; pending?: DecimalString; available?: DecimalString; paid?: DecimalString };
}
export interface CostsResponse {
  days: number;
  series: DailyPoint[];
  byDataset: { datasetId: string; name?: string; hostingCost: DecimalString; servingCost?: DecimalString; storageBytes?: number }[];
  /** servingCost is measured from the ledger; hosting figures are modelled monthly estimates. */
  totals?: { hostingCost: DecimalString | null; servingCost?: DecimalString };
  estimate?: { low: DecimalString; high: DecimalString; expected: DecimalString } | null;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix?: string;
  scopes: string[];
  maxPricePerQuery?: DecimalString | null;
  allowedDatasets?: string[] | null;
  status: "active" | "revoked" | string;
  createdAt: string;
  lastUsedAt?: string | null;
  /** The secret can be shown again (keys created before reveal existed cannot). */
  revealable?: boolean;
}
export interface Balance {
  funded: DecimalString;
  captured: DecimalString;
  reserved: DecimalString;
  available: DecimalString;
  monthSpend: DecimalString;
  monthlyLimit: DecimalString | null;
}
export interface BillingResponse {
  balance: Balance | null;
  budget: { monthlyLimit: DecimalString | null } | null;
  transactions: { txId: string; type: string; ts: string; amount: DecimalString; ref?: Record<string, string> }[];
  /** "stripe" = Add credits opens Stripe Checkout; "dev" = credited instantly (non-production). */
  paymentProvider?: string;
  /** Set when Stripe's embedded Checkout can be shown in the page. */
  stripePublishableKey?: string | null;
}
export interface ConsumerUsageResponse {
  days: number;
  series: DailyPoint[];
  byDataset: { datasetId: string; name?: string; providerName?: string; queries: number; spend: DecimalString; avgLatencyMs?: number }[];
  recent: UsageEventRow[];
  totals?: { queries: number; spend: DecimalString; cacheHitRate: number | null; p95LatencyMs: number | null };
}
export interface AuditEvent {
  id: string;
  ts: string;
  actorId: string | null;
  actorEmail?: string | null;
  action: string;
  targetType?: string;
  targetId?: string;
  before?: unknown;
  after?: unknown;
  requestId?: string;
  ip?: string | null;
}
export interface Member { userId: string; email: string; name: string; role: Role; joinedAt?: string }

export interface CatalogEntry {
  datasetId: string;
  providerId: string;
  providerName: string;
  name: string;
  description: string;
  categories: string[];
  keywords: string[];
  geographic: string[];
  capabilities: { name: string; description: string; kind: string; examples: string[]; filters: string[] }[];
  freshnessTargetSeconds: number | null;
  pricePerQueryMicros: number;
  scores: { quality: number; completeness: number; reliability: number; freshness: number; exclusivity: number };
  estimatedLatencyMs: number;
  rights: { liveQuery: boolean; rag: boolean; training: boolean };
  freeSample: boolean;
  status: "active";
}
export interface MarketplaceDatasetResponse {
  manifest: DatasetManifest | SignedEnvelope<DatasetManifest>;
  status: DatasetStatus | null;
  competition?: { similarCount: number; tier?: string } | null;
}

/** Accept both a bare manifest and a signed envelope. */
export function unwrapManifest(m: DatasetManifest | SignedEnvelope<DatasetManifest> | null | undefined): DatasetManifest | null {
  if (!m) return null;
  return "payload" in m && "signature" in m ? m.payload : (m as DatasetManifest);
}


// ---------- response normalisation ----------
// The control plane returns ledger/DB-shaped payloads (snake_case rows, `daily`/`day`, micros). These adapters map
// them onto the view models above WITHOUT inventing values: anything absent stays null/undefined and renders as "—".
type AnyObj = Record<string, any>;
const dec = (v: unknown): DecimalString | undefined => (typeof v === "string" ? v : typeof v === "number" && Number.isSafeInteger(v) ? formatMicros(v) : undefined);
const sumDec = (xs: (string | undefined)[]): DecimalString => formatMicros(xs.reduce((a, x) => a + (x ? Math.round(Number(x) * 1_000_000) : 0), 0));

function normDataset(d: AnyObj): DatasetSummary {
  return {
    ...d,
    id: d.id, name: d.name, status: d.status,
    pricePerQuery: d.pricePerQuery ?? (typeof d.pricePerQueryMicros === "number" ? formatMicros(d.pricePerQueryMicros) : undefined),
    activeContractVersion: d.activeContractVersion ?? d.activeVersion ?? null,
    freshnessStatus: d.freshnessStatus ?? null,
  } as DatasetSummary;
}
function normVersion(v: AnyObj): ContractVersionInfo {
  return {
    version: v.version, status: v.status, createdAt: v.createdAt ?? v.created_at, createdBy: v.createdBy ?? v.created_by,
    approvedBy: Array.isArray(v.approvedBy) ? v.approvedBy : v.approved_by ? [{ userId: v.approved_by, role: "", at: v.approved_at }] : [],
  };
}
function normDetail(r: AnyObj): DatasetDetail {
  return {
    ...r,
    dataset: normDataset(r.dataset),
    // control plane wraps the contract with its version metadata: { version, status, hash, violations, contract }
    contract: r.contract && "entities" in r.contract ? r.contract : (r.contract?.contract ?? null),
    contractMeta: r.contract && !("entities" in r.contract) ? { version: r.contract.version, status: r.contract.status, hash: r.contract.hash, violations: r.contract.violations ?? [] } : null,
    versions: (r.versions ?? []).map(normVersion),
    approvals: (r.approvals ?? []).map((a: AnyObj) => ({ version: a.version, userId: a.userId ?? a.user_id, role: a.role, at: a.at ?? a.created_at })),
  } as DatasetDetail;
}
function normOverview(r: AnyObj): MetricOverview {
  const p = r.provider, c = r.consumer;
  return {
    provider: p ? { ...p, avgFreshnessSeconds: p.avgFreshnessSeconds ?? p.averageFreshnessSeconds ?? null, cacheHitRate: p.cacheHitRate ?? null } : undefined,
    consumer: c ? { ...c, avgPrice: c.avgPrice ?? c.averagePrice, p95LatencyMs: c.p95LatencyMs ?? c.p95LatencyMs24h ?? null, cacheHitRate: c.cacheHitRate ?? null } : undefined,
  };
}
function normRevenue(r: AnyObj): RevenueResponse {
  const daily: AnyObj[] = r.series ?? r.daily ?? [];
  const series: DailyPoint[] = daily.map((d) => ({ date: d.date ?? d.day, queries: d.queries ?? 0, cacheHits: d.cacheHits, gross: d.gross, fee: d.fee, net: d.revenue ?? d.net, hostingCost: d.servingCost }));
  return {
    days: r.days,
    series,
    byDataset: (r.byDataset ?? []).map((d: AnyObj) => ({ datasetId: d.datasetId, name: d.name, queries: d.queries, gross: d.gross, net: d.revenue ?? d.net })),
    byConsumer: (r.byConsumer ?? []).map((d: AnyObj) => ({ consumerOrgId: d.consumer ?? d.consumerOrgId, queries: d.queries, gross: d.gross })),
    totals: r.totals ?? {
      queries: series.reduce((a, d) => a + d.queries, 0),
      gross: sumDec(series.map((d) => d.gross)), fee: sumDec(series.map((d) => d.fee)), net: sumDec(series.map((d) => d.net)),
      pending: r.earnings?.pending, available: r.earnings?.available, paid: r.earnings?.paid,
    },
  };
}
function normCosts(r: AnyObj): CostsResponse {
  const daily: AnyObj[] = r.series ?? r.daily ?? [];
  const per: AnyObj[] = r.byDataset ?? r.perDataset ?? [];
  const est = per.map((d) => d.estimate).filter(Boolean) as AnyObj[];
  return {
    days: r.days,
    series: daily.map((d) => ({ date: d.date ?? d.day, queries: d.queries ?? 0, hostingCost: d.hostingCost ?? d.servingCost })),
    byDataset: per.map((d) => ({ datasetId: d.datasetId, name: d.name, hostingCost: d.hostingCost ?? d.estimate?.expected, storageBytes: d.storageBytes })),
    totals: { hostingCost: r.totals?.hostingCost ?? null, servingCost: r.totals?.servingCost ?? sumDec(daily.map((d) => d.servingCost)) },
    estimate: est.length ? { low: sumDec(est.map((e) => e.low)), high: sumDec(est.map((e) => e.high)), expected: sumDec(est.map((e) => e.expected)) } : null,
  };
}
function normCostEstimate(r: AnyObj): CostEstimateResponse {
  const e: AnyObj = r.estimate ?? r;
  return {
    low: e.low, high: e.high, expected: e.expected,
    breakdown: (e.breakdown ?? []).map((b: AnyObj) => ({ product: b.product, quantity: b.quantity, unit: b.unit, cost: b.cost ?? formatMicros(b.costMicros ?? 0), note: b.note })),
    assumptions: e.assumptions ?? [], tableVersion: e.tableVersion,
    scenarios: (r.scenarios ?? []).map((s: AnyObj) => ({
      qpm: s.qpm, queriesPerMonth: s.queriesPerMonth,
      gross: dec(s.grossMicros) ?? s.gross, fee: dec(s.feeMicros) ?? s.fee, providerGross: dec(s.providerGrossMicros) ?? s.providerGross,
      hosting: dec(s.hostingMicros) ?? s.hosting, net: dec(s.netMicros) ?? s.net,
    })),
  };
}
function normRecommendation(r: AnyObj): PriceRecommendation {
  return { ...r, forecast: r.forecast ?? r.demand } as PriceRecommendation;
}
function normUsageRow(u: AnyObj): UsageEventRow {
  return {
    queryId: u.queryId ?? u.query_id, ts: u.ts, datasetId: u.datasetId ?? u.dataset_id ?? "", capability: u.capability,
    consumerOrgId: u.consumerOrgId ?? u.consumer, price: u.price, providerShare: u.providerShare ?? u.providerEarnings,
    cacheStatus: u.cacheStatus ?? u.cache_status, latencyMs: u.latencyMs ?? u.latency_ms, rows: u.rows, status: u.status,
    routingReason: u.routingReason ?? u.routing_reason ?? null,
  };
}
function normConsumerUsage(r: AnyObj): ConsumerUsageResponse {
  const daily: AnyObj[] = r.series ?? r.daily ?? [];
  const series: DailyPoint[] = daily.map((d) => ({ date: d.date ?? d.day, queries: d.queries ?? 0, cacheHits: d.cacheHits, errors: d.errors, spend: d.spend, latencyMsAvg: d.avgLatencyMs ?? d.latencyMsAvg ?? null }));
  const q = series.reduce((a, d) => a + d.queries, 0), h = series.reduce((a, d) => a + (d.cacheHits ?? 0), 0);
  return {
    days: r.days, series,
    byDataset: (r.byDataset ?? []).map((d: AnyObj) => ({ datasetId: d.datasetId, name: d.name, providerName: d.providerName, queries: d.queries, spend: d.spend, avgLatencyMs: d.avgLatencyMs })),
    recent: (r.recent ?? []).map(normUsageRow),
    totals: r.totals ?? { queries: q, spend: sumDec(series.map((d) => d.spend)), cacheHitRate: q ? h / q : null, p95LatencyMs: null },
  };
}
function normAudit(e: AnyObj): AuditEvent {
  return {
    id: e.id, ts: e.ts ?? e.created_at, actorId: e.actorId ?? e.actor_user_id ?? null, actorEmail: e.actorEmail ?? e.actor_email ?? null,
    action: e.action, targetType: e.targetType ?? e.target_type, targetId: e.targetId ?? e.target_id, before: e.before, after: e.after,
    requestId: e.requestId ?? e.request_id, ip: e.ip ?? null,
  };
}
function normMember(m: AnyObj): Member {
  return { userId: m.userId ?? m.id, email: m.email, name: m.name, role: m.role, joinedAt: m.joinedAt ?? m.created_at };
}
function normSourceDetail(r: AnyObj): SourceDetail {
  const topo = Array.isArray(r.topology) ? Object.fromEntries(r.topology.map((t: AnyObj) => [t.id, { status: t.status, detail: t.detail }])) : r.topology;
  return { ...r, datasets: (r.datasets ?? []).map(normDataset), topology: topo } as SourceDetail;
}

// ---------- transport ----------
async function parseError(res: Response): Promise<ApiError> {
  let code = "http_" + res.status;
  let message = res.statusText || "Request failed";
  let requestId = res.headers.get("x-request-id");
  let retryable = res.status >= 500;
  let details: Record<string, unknown> | null = null;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string; request_id?: string; retryable?: boolean; details?: Record<string, unknown> } };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      requestId = body.error.request_id ?? requestId;
      retryable = body.error.retryable ?? retryable;
      details = body.error.details ?? null;
    }
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, code, message, requestId, retryable, details);
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export async function request<T>(method: Method, path: string, body?: unknown, init?: { signal?: AbortSignal }): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET") headers["X-DBIndex-CSRF"] = "1";
  let res: Response;
  try {
    res = await fetch("/api" + path, {
      method,
      credentials: "include",
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: init?.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Could not reach the DBIndex control plane.", null, true);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  const s = u.toString();
  return s ? "?" + s : "";
}

// ---------- control plane ----------
export const api = {
  auth: {
    signup: (b: { email: string; password: string; name: string; orgName: string; orgKind: "provider" | "consumer" | "both"; gtm?: Record<string, unknown> }) =>
      request<{ user: User; org: Org }>("POST", "/auth/signup", b),
    login: (b: { email: string; password: string }) => request<{ user: User }>("POST", "/auth/login", b),
    logout: () => request<void>("POST", "/auth/logout", {}),
    providers: () => request<{ password: boolean; privy: { appId: string } | null; hostedConnections?: boolean }>("GET", "/auth/providers"),
    /** Exchange a Privy sign-in for a DBIndex session; first-time users get `needsOnboarding` until they name an org. */
    privy: (b: { identityToken?: string; accessToken?: string; name?: string; orgName?: string; orgKind?: "provider" | "consumer" | "both"; gtm?: Record<string, unknown> }) =>
      request<{ needsOnboarding: true; email: string; suggestedName: string } | { needsOnboarding?: undefined; user: User; org?: Org; linked?: boolean }>("POST", "/auth/privy", b),
    me: (signal?: AbortSignal) => request<Me>("GET", "/me", undefined, { signal }),
  },
  orgs: {
    create: (b: { name: string; kinds: OrgKind[] }) => request<{ org: Org }>("POST", "/orgs", b),
  },
  public: {
    marketplace: (p: { q?: string; category?: string; country?: string; maxPrice?: string; minQuality?: number; realtime?: boolean } = {}) =>
      request<{ datasets: CatalogEntry[] }>("GET", "/public/marketplace" + qs(p)),
    dataset: (id: string) => request<MarketplaceDatasetResponse>("GET", `/public/marketplace/${encodeURIComponent(id)}`),
  },
  org(orgId: string) {
    const base = `/orgs/${encodeURIComponent(orgId)}`;
    const p = (s: string) => base + s;
    return {
      overview: () => request<AnyObj>("GET", p("/overview")).then(normOverview),
      members: {
        list: () => request<{ members: AnyObj[] }>("GET", p("/members")).then((r) => ({ members: r.members.map(normMember) })),
        add: (b: { email: string; role: Role }) => request<{ member: Member }>("POST", p("/members"), b),
        update: (userId: string, role: Role) => request<{ member: Member }>("PATCH", p(`/members/${encodeURIComponent(userId)}`), { role }),
        remove: (userId: string) => request<void>("DELETE", p(`/members/${encodeURIComponent(userId)}`)),
      },
      sources: {
        list: () => request<{ sources: Source[] }>("GET", p("/sources")),
        create: (b: { name: string; engine: SourceEngine; connectivity: Connectivity; dsn?: string }) => request<CreateSourceResponse>("POST", p("/sources"), b),
        updateCredentials: (id: string, dsn: string) => request<{ ok: boolean }>("PUT", p(`/sources/${encodeURIComponent(id)}/credentials`), { dsn }),
        /** Stop reading the database: datasets paused, connector revoked, stored connection string destroyed. */
        disconnect: (id: string) => request<{ ok: boolean; pausedDatasets: string[] }>("POST", p(`/sources/${encodeURIComponent(id)}/disconnect`), {}),
        /** Delete the connection and everything DBIndex holds about it (datasets retired, serving copies purged). */
        remove: (id: string, confirmName: string) => request<{ ok: boolean; retiredDatasets: string[] }>("DELETE", p(`/sources/${encodeURIComponent(id)}`), { confirmName }),
        get: (id: string) => request<AnyObj>("GET", p(`/sources/${encodeURIComponent(id)}`)).then(normSourceDetail),
        enrollmentToken: (id: string) =>
          request<{ enrollment: { token: string; expiresAt: string }; setup?: SetupInstructions; connect?: ConnectInfo }>("POST", p(`/sources/${encodeURIComponent(id)}/enrollment-token`), {}),
        test: (id: string) => request<{ job: Job }>("POST", p(`/sources/${encodeURIComponent(id)}/test`), {}),
        inspect: (id: string) => request<{ job: Job }>("POST", p(`/sources/${encodeURIComponent(id)}/inspect`), {}),
        job: (id: string, jobId: string) => request<{ job: Job }>("GET", p(`/sources/${encodeURIComponent(id)}/jobs/${encodeURIComponent(jobId)}`)),
        schema: (id: string) => request<{ snapshot: SchemaSnapshot | null; capturedAt: string | null }>("GET", p(`/sources/${encodeURIComponent(id)}/schema`)),
        runArchitect: (id: string, tables?: string[]) =>
          request<{ proposal: ArchitectProposal }>("POST", p(`/sources/${encodeURIComponent(id)}/architect`), tables ? { tables } : {}),
        architect: (id: string) => request<{ proposal: ArchitectProposal | null }>("GET", p(`/sources/${encodeURIComponent(id)}/architect`)),
        /** AI schema mapping: suggested tables, target names, excluded columns and row filters, for review (nothing is saved). */
        suggestMapping: <D extends MappingDraft>(id: string, b: { draft: D; included: string[]; goal?: string }) =>
          request<MappingSuggestion<D>>("POST", p(`/sources/${encodeURIComponent(id)}/mapping/suggest`), b),
      },
      datasets: {
        list: () => request<{ datasets: AnyObj[] }>("GET", p("/datasets")).then((r) => ({ datasets: r.datasets.map(normDataset) })),
        create: (b: {
          sourceId: string;
          name: string;
          description: string;
          categories: string[];
          keywords: string[];
          geographic: string[];
          freshness: Freshness;
          contractDraft: unknown;
          visibility?: "private" | "network";
        }) => request<{ dataset: DatasetSummary; contractVersion: number | ContractVersionInfo }>("POST", p("/datasets"), b),
        get: (id: string) => request<AnyObj>("GET", p(`/datasets/${encodeURIComponent(id)}`)).then(normDetail),
        putContract: (id: string, contract: unknown) => request<PutContractResponse>("PUT", p(`/datasets/${encodeURIComponent(id)}/contract`), { contract }),
        exposureReport: (id: string) => request<{ report: ExposureReportEntity[] } | ExposureReportEntity[]>("GET", p(`/datasets/${encodeURIComponent(id)}/exposure-report`)),
        simulate: (id: string, question: string) => request<SimulateResponse>("POST", p(`/datasets/${encodeURIComponent(id)}/simulate`), { question }),
        approve: (id: string, version: number) => request<{ ok: boolean }>("POST", p(`/datasets/${encodeURIComponent(id)}/approve`), { version }),
        activate: (id: string) => request<{ ok: boolean; dataset?: DatasetSummary }>("POST", p(`/datasets/${encodeURIComponent(id)}/activate`), {}),
        setVisibility: (id: string, visibility: "private" | "network") =>
          request<{ dataset: AnyObj }>("PUT", p(`/datasets/${encodeURIComponent(id)}/visibility`), { visibility }).then((r) => ({ dataset: normDataset(r.dataset) })),
        /** Money this dataset has generated: all time, last 30 days and today (revenue is after the DBIndex fee). */
        earnings: (id: string) => request<DatasetEarnings>("GET", p(`/datasets/${encodeURIComponent(id)}/earnings`)),
        /** Re-read every mapped table from the database and rebuild the replica and its indexes; it keeps serving meanwhile. */
        resync: (id: string) => request<{ syncJobId: string }>("POST", p(`/datasets/${encodeURIComponent(id)}/resync`), {}),
        /** Stop syncing and serving; progress is kept so resume continues where it stopped. */
        pause: (id: string) => request<{ ok: boolean }>("POST", p(`/datasets/${encodeURIComponent(id)}/pause`), {}),
        setPricing: (id: string, b: { pricePerQuery: DecimalString; tiers: { fromQueriesPerMonth: number; pricePerQuery: DecimalString }[] }) =>
          request<{ ok: boolean }>("PUT", p(`/datasets/${encodeURIComponent(id)}/pricing`), b),
        pricingRecommendation: (id: string) => request<AnyObj>("GET", p(`/datasets/${encodeURIComponent(id)}/pricing/recommendation`)).then(normRecommendation),
        costEstimate: (id: string, freshness?: Freshness) =>
          request<AnyObj>("GET", p(`/datasets/${encodeURIComponent(id)}/cost-estimate`) + qs({ freshness })).then(normCostEstimate),
        queries: (id: string, limit = 100) => request<{ queries: AnyObj[] }>("GET", p(`/datasets/${encodeURIComponent(id)}/queries`) + qs({ limit })).then((r) => ({ queries: r.queries.map(normUsageRow) })),
      },
      revenue: (days = 30) => request<AnyObj>("GET", p("/revenue") + qs({ days })).then(normRevenue),
      costs: (days = 30) => request<AnyObj>("GET", p("/costs") + qs({ days })).then(normCosts),
      apiKeys: {
        list: () => request<{ keys: ApiKey[] }>("GET", p("/api-keys")),
        create: (b: { name: string; scopes: string[]; maxPricePerQuery?: DecimalString; allowedDatasets?: string[] }) =>
          request<{ key: ApiKey; secret: string }>("POST", p("/api-keys"), b),
        revoke: (keyId: string) => request<void>("DELETE", p(`/api-keys/${encodeURIComponent(keyId)}`)),
        /** Show an active key's secret again (key managers only; audited). */
        reveal: (keyId: string) => request<{ secret: string }>("POST", p(`/api-keys/${encodeURIComponent(keyId)}/reveal`), {}),
      },
      consumer: {
        usage: (days = 30) => request<AnyObj>("GET", p("/consumer/usage") + qs({ days })).then(normConsumerUsage),
        billing: () => request<BillingResponse>("GET", p("/consumer/billing")),
        addCredits: (amount: DecimalString, embedded = false) => request<{ paymentId?: string; checkoutUrl?: string; clientSecret?: string; balance?: Balance }>("POST", p("/consumer/credits"), { amount, embedded }),
        /** After Stripe Checkout: settle the payment now instead of waiting for the webhook. */
        confirmPayment: (paymentId: string) => request<{ paymentId: string; status: "pending" | "succeeded" | "failed"; balance: Balance | null }>("POST", p(`/consumer/payments/${encodeURIComponent(paymentId)}/confirm`), {}),
        setBudget: (monthlyLimit: DecimalString | null) => request<{ balance?: Balance }>("PUT", p("/consumer/budget"), { monthlyLimit }),
      },
      audit: (cursor?: string) => request<AnyObj>("GET", p("/audit") + qs({ cursor })).then((r) => ({ events: (r.events ?? []).map(normAudit), nextCursor: (r.nextCursor ?? r.next_cursor ?? null) as string | null })),
    };
  },
};
export type OrgApi = ReturnType<typeof api.org>;

// ---------- data plane ----------
export interface RankedCandidate {
  datasetId: string;
  providerId: string;
  providerName: string;
  datasetName: string;
  capability: string;
  relevance: number;
  pricePerQueryMicros: number;
  freshnessTargetSeconds: number | null;
  actualLagSeconds: number | null;
  estimatedLatencyMs: number;
  scores: { quality: number; completeness: number; reliability: number; freshness: number; exclusivity: number };
  score: number;
  utility: number;
  eligible: boolean;
  rejectedBecause: string[];
  explanation: Record<string, number>;
}
export interface QueryConstraintsInput {
  freshness_seconds?: number;
  max_query_price?: DecimalString;
  max_latency_ms?: number;
  quality_min?: number;
  preference?: "balanced" | "cheapest" | "fastest" | "freshest" | "highest_quality";
  datasets?: string[];
  /** Accept a labelled closest-match answer when nothing matches exactly (only ever applied to free data). */
  allow_closest?: boolean;
}
export interface DataPlaneQueryResponse {
  data: { columns: string[]; rows: unknown[][]; row_count: number; truncated: boolean; next_cursor: string | null };
  dbindex: {
    plan: unknown; plan_source: string; routing: unknown;
    match?: "exact" | "closest" | "overview";
    closest?: { answered: string; dropped: string[]; suggestions: string[] };
  };
  provenance: {
    dataset_id: string;
    dataset_name: string;
    provider: string;
    provider_id: string;
    schema_version: number;
    contract_version: number;
    source_timestamp: string | null;
    query_timestamp: string;
    freshness_seconds: number | null;
    quality_score: number;
    transformations: string[];
    license: string;
    rights: Record<string, unknown>;
    cache_status: string;
  };
  billing: { query_price: DecimalString; currency: "USD"; charged: boolean; idempotent_replay?: boolean };
  request_id: string;
}

async function dp<T>(apiKey: string, path: string, body: unknown, extraHeaders: Record<string, string> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      credentials: "omit",
      headers: { authorization: `Bearer ${apiKey}`, "content-type": "application/json", accept: "application/json", ...extraHeaders },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network_error", "Could not reach the DBIndex data plane.", null, true);
  }
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

/** /v1 is snake_case on the wire (public API convention); map to the camelCase view model. */
function normCandidate(c: AnyObj): RankedCandidate {
  const pick = (camel: string, snake: string) => (c[camel] !== undefined ? c[camel] : c[snake]);
  const priceDec = pick("pricePerQuery", "price_per_query");
  return {
    datasetId: pick("datasetId", "dataset_id"), providerId: pick("providerId", "provider_id"), providerName: pick("providerName", "provider"),
    datasetName: pick("datasetName", "dataset_name"), capability: c.capability, relevance: c.relevance ?? 0,
    pricePerQueryMicros: pick("pricePerQueryMicros", "price_per_query_micros") ?? (typeof priceDec === "string" ? Math.round(Number(priceDec) * 1_000_000) : 0),
    freshnessTargetSeconds: pick("freshnessTargetSeconds", "freshness_target_seconds") ?? null,
    actualLagSeconds: pick("actualLagSeconds", "actual_lag_seconds") ?? null,
    estimatedLatencyMs: pick("estimatedLatencyMs", "estimated_latency_ms") ?? 0,
    scores: c.scores ?? { quality: c.quality ?? 0, completeness: c.explanation?.completeness ?? 0, reliability: c.explanation?.reliability ?? 0, freshness: c.explanation?.freshness ?? 0, exclusivity: c.explanation?.exclusivity ?? 0 },
    score: c.score ?? 0, utility: c.utility ?? 0, eligible: c.eligible !== false,
    rejectedBecause: pick("rejectedBecause", "rejected_because") ?? [], explanation: c.explanation ?? {},
  };
}

export const dataPlane = {
  discover: (apiKey: string, b: { question: string; constraints?: QueryConstraintsInput; limit?: number }) =>
    dp<{ candidates: AnyObj[]; request_id: string }>(apiKey, "/v1/discover", b).then((r) => ({ ...r, candidates: (r.candidates ?? []).map(normCandidate) })),
  plan: (apiKey: string, b: { question: string; constraints?: QueryConstraintsInput }) =>
    dp<{ plan: QueryPlan; plan_source: string; candidates: AnyObj[]; estimated_price: DecimalString }>(apiKey, "/v1/plan", b).then((r) => ({ ...r, candidates: (r.candidates ?? []).map(normCandidate) })),
  query: (apiKey: string, b: { question?: string; plan?: QueryPlan; constraints?: QueryConstraintsInput }, idempotencyKey?: string) =>
    dp<DataPlaneQueryResponse>(apiKey, "/v1/query", b, idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
};
